// frontend/src/components/EnhancedInsuranceIcons/AutoInsuranceIcon.jsx
import React, { useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import styles from './EnhancedInsuranceIcons.module.css';

const AutoInsuranceIcon = ({ size = 120 }) => {
  const carRef = useRef(null);
  
  useEffect(() => {
    if (!carRef.current) return;
    
    // Get all car parts
    const wheels = carRef.current.querySelector('#wheels');
    const rearWheel = carRef.current.querySelector('#rear-wheel');
    const frontWheel = carRef.current.querySelector('#front-wheel');
    const carGroup = carRef.current.querySelector('#car-group');
    const highlights = carRef.current.querySelector('#highlights');
    
    // Hide all elements initially
    gsap.set([wheels, carGroup, highlights], { 
      opacity: 0,
      y: -20
    });
    
    // Create animation timeline
    const tl = gsap.timeline({ defaults: { ease: "back.out(1.7)" } });
    
    // First animate the wheels coming in
    tl.to(wheels, { 
      opacity: 1, 
      y: 0, 
      duration: 0.6, 
      delay: 0.2 
    });
    
    // Then animate the car body dropping onto the wheels
    tl.to(carGroup, { 
      opacity: 1, 
      y: 0, 
      duration: 0.6 
    }, "-=0.2");
    
    // Then fade in the highlights for 3D effect
    tl.to(highlights, {
      opacity: 1,
      duration: 0.4
    }, "-=0.3");
    
    // Add subtle continuous animation for a "hovering" effect
    gsap.to(carGroup, {
      y: "-=2",
      duration: 1.8,
      repeat: -1,
      yoyo: true,
      ease: "sine.inOut",
      delay: 0.5
    });
    
    // Highlights should move with the car body
    gsap.to(highlights, {
      y: "-=2",
      duration: 1.8,
      repeat: -1,
      yoyo: true,
      ease: "sine.inOut",
      delay: 0.5
    });
    
    // Add wheel rotation animation
    gsap.to([frontWheel, rearWheel], {
      rotation: 360,
      transformOrigin: "50% 50%",
      duration: 8,
      repeat: -1,
      ease: "none",
      delay: 0.5
    });
    
    return () => {
      // Clean up animations when component unmounts
      tl.kill();
      gsap.killTweensOf([wheels, carGroup, highlights, frontWheel, rearWheel]);
    };
  }, []);
  
  return (
    <div className={styles.iconContainer} ref={carRef} style={{ width: size, height: size }}>
      <svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
        {/* Definitions for gradients and filters */}
        <defs>
          <linearGradient id="bodyGradient" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="#3357FF" />
            <stop offset="100%" stopColor="#2348ED" />
          </linearGradient>
          
          <linearGradient id="wheelGradient" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#444444" />
            <stop offset="100%" stopColor="#222222" />
          </linearGradient>
          
          <linearGradient id="windowGradient" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="#C2D6FF" />
            <stop offset="100%" stopColor="#A0C4FF" />
          </linearGradient>
          
          <filter id="shadow" x="-20%" y="-20%" width="140%" height="140%">
            <feDropShadow dx="0" dy="5" stdDeviation="3" floodOpacity="0.3" />
          </filter>
          
          <filter id="glow" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="1" result="blur" />
            <feFlood floodColor="#FFF9C4" floodOpacity="0.5" />
            <feComposite in2="blur" operator="in" />
            <feComposite in="SourceGraphic" />
          </filter>
        </defs>
        
        {/* Drop shadow for the entire car */}
        <ellipse 
          cx="45" 
          cy="85" 
          rx="40" 
          ry="5" 
          fill="rgba(0,0,0,0.15)" 
          filter="blur(3px)"
        />
        
        {/* Wheels group - KEEP POSITION THE SAME */}
        <g id="wheels">
          {/* Rear wheel with 3D effect */}
          <g id="rear-wheel" transform="translate(20, 75)">
            <circle cx="0" cy="0" r="15" fill="url(#wheelGradient)" filter="url(#shadow)" />
            <circle cx="0" cy="0" r="7" fill="#777777" />
            <circle cx="0" cy="0" r="5" fill="#555555" />
            <path d="M0,-11 L0,11 M-11,0 L11,0" stroke="#AAAAAA" strokeWidth="2" />
            {/* Wheel rim highlight */}
            <path 
              d="M-8,-8 A12,12 0 0,1 8,-8" 
              stroke="rgba(255,255,255,0.3)" 
              strokeWidth="2" 
              fill="none"
            />
          </g>
          
          {/* Front wheel with 3D effect */}
          <g id="front-wheel" transform="translate(70, 75)">
            <circle cx="0" cy="0" r="15" fill="url(#wheelGradient)" filter="url(#shadow)" />
            <circle cx="0" cy="0" r="7" fill="#777777" />
            <circle cx="0" cy="0" r="5" fill="#555555" />
            <path d="M0,-11 L0,11 M-11,0 L11,0" stroke="#AAAAAA" strokeWidth="2" />
            {/* Wheel rim highlight */}
            <path 
              d="M-8,-8 A12,12 0 0,1 8,-8" 
              stroke="rgba(255,255,255,0.3)" 
              strokeWidth="2" 
              fill="none"
            />
          </g>
        </g>
        
        {/* Car body group - KEEP POSITION THE SAME FOR MAIN BODY */}
        <g id="car-group">
          {/* Car body with 3D-style gradient */}
          <path 
            id="car-body" 
            d="M10,89 h80 a15,30 0 0,0 0,-15 h-20 l-10,-15 h-40 l-10,15 h-20 a15,30 0 0,0 0,15 z" 
            fill="url(#bodyGradient)" 
            filter="url(#shadow)"
          />
          
          {/* Windshield with 3D gradient - MOVED 10px LEFT */}
          <path 
            id="windshield" 
            d="M20,60 h40 l5,15 h-50 z" 
            fill="url(#windowGradient)" 
            opacity="0.8"
          />
          
          {/* Side window with 3D gradient - MOVED 10px LEFT */}
          <rect 
            id="side-window" 
            x="62" 
            y="64" 
            width="8" 
            height="10" 
            rx="2" 
            fill="url(#windowGradient)" 
            opacity="0.8"
          />
          
          {/* Headlight with glow - ADJUSTED POSITION */}
          <ellipse 
            id="headlight" 
            cx="85" 
            cy="75" 
            rx="3" 
            ry="3" 
            fill="#FFF9C4" 
            filter="url(#glow)"
          />
          
          {/* Taillight with glow - ADJUSTED POSITION */}
          <rect 
            id="taillight" 
            x="12" 
            y="75" 
            width="3" 
            height="5" 
            rx="1" 
            fill="#DF1C41"
            filter="url(#glow)"
          />
          
          {/* Car door line for detail - MOVED LEFT */}
          <line 
            x1="40" 
            y1="60" 
            x2="40" 
            y2="89" 
            stroke="rgba(0,0,0,0.1)" 
            strokeWidth="1"
          />
          
          {/* Car handle - MOVED LEFT */}
          <rect
            x="45"
            y="70"
            width="6"
            height="2"
            rx="1"
            fill="rgba(0,0,0,0.2)"
          />
        </g>
        
        {/* Highlights to enhance 3D effect - MOVED LEFT */}
        <g id="highlights">
          {/* Top edge highlight */}
          <path 
            d="M10,59 h50 l10,15"
            stroke="rgba(255,255,255,0.6)" 
            strokeWidth="1.5"
            fill="none"
            strokeLinecap="round"
          />
          
          {/* Bottom edge highlight */}
          <path 
            d="M10,85 h60"
            stroke="rgba(0,0,0,0.15)" 
            strokeWidth="1"
            fill="none"
          />
          
          {/* Windshield reflection */}
          <path 
            d="M25,65 l25,-3"
            stroke="rgba(255,255,255,0.5)" 
            strokeWidth="3"
            fill="none"
            strokeLinecap="round"
          />
        </g>
      </svg>
    </div>
  );
};

export default AutoInsuranceIcon;